import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, Loader, Schema, SelectPicker,
} from 'rsuite';
import { ItemDataType } from 'rsuite/CascadeTree';
import axios from 'axios';
import { showSuccessMessage } from '../utils/message';
import { utils } from '../utils/utils';
import { useStoreState } from '../store/hooks';
import { Permission } from '../store/user/types';
import { WebEdiConfiguration, WebEdiIntegrationSettings } from '../api/webedi/webEdiApiTypes';
import {
  getWebEdiConfigurations,
  getWebEdiIntegrationSettings,
  saveWebEdiIntegrationSettings,
} from '../api/webedi/webEdi';
import ApiTokenReset from '../mpi/ApiTokenReset';

const model = Schema.Model({
  partnerReceiverGln: Schema.Types.StringType().isRequired('Receiver GLN is required.'),
  partnerSupplierGln: Schema.Types.StringType().isRequired('Supplier GLN is required.'),
});

interface Edit {
  partnerReceiverGln: string;
  partnerSupplierGln: string;
  configuredGln: string;
}

interface State {
  status: 'ready' | 'loading' | 'saving',
  integrationSettings?: WebEdiIntegrationSettings,
  webEdiConfigurations: WebEdiConfiguration[],
  edit: Edit,
}

const initialState: State = {
  status: 'ready',
  webEdiConfigurations: [],
  edit: {
    partnerReceiverGln: '',
    partnerSupplierGln: '',
    configuredGln: '',
  },
};

type StatusSetAction = { type: 'status/set', payload: 'ready' | 'loading' | 'saving' };
type WebEdiIntegrationSettingsSetAction = { type: 'webEdiIntegrationSettings/set', payload: WebEdiIntegrationSettings };
type WebEdiConfigurationsSetAction = { type: 'webEdiConfigurations/set', payload: WebEdiConfiguration[] };
type EditSetAction = { type: 'edit/set', payload: Edit };
type Action = StatusSetAction
| WebEdiIntegrationSettingsSetAction
| WebEdiConfigurationsSetAction
| EditSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set':
      return { ...state, status: action.payload };
    case 'webEdiIntegrationSettings/set':
      return {
        ...state,
        integrationSettings: action.payload,
        edit: {
          partnerReceiverGln: utils.defaultToEmpty(action.payload.partnerReceiverGln),
          partnerSupplierGln: utils.defaultToEmpty(action.payload.partnerSupplierGln),
          configuredGln: utils.defaultToEmpty(action.payload.configuredGln),
        },
      };
    case 'webEdiConfigurations/set':
      return {
        ...state,
        webEdiConfigurations: action.payload,
      };
    case 'edit/set':
      return { ...state, edit: action.payload };
    default:
      return state;
  }
}

function mapEditToIntegrationSettings(edit: Edit): WebEdiIntegrationSettings {
  return {
    partnerReceiverGln: edit.partnerReceiverGln || undefined,
    partnerSupplierGln: edit.partnerSupplierGln || undefined,
    configuredGln: edit.configuredGln || undefined,
  };
}

function mapWebEdiConfigurations(webEdiConfigurations: WebEdiConfiguration[]): ItemDataType[] {
  return webEdiConfigurations
    .map(webEdiConfiguration => ({
      value: webEdiConfiguration.gln,
      label: webEdiConfiguration.gln,
    }));
}

export default function WebEdiIntegrationSetup() {
  const currentUser = useStoreState(state => state.user);
  const [state, dispatch] = useReducer(reducer, initialState);
  const formRef = useRef<any>();

  async function fetchData() {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const [integrationSettings, webEdiConfigurations] = await Promise.all([
        getWebEdiIntegrationSettings().catch(error => {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            return {
              partnerReceiverGln: undefined,
              partnerSupplierGln: undefined,
              configuredGln: undefined,
            };
          }
          throw error;
        }),
        getWebEdiConfigurations(),
      ]);

      dispatch({ type: 'webEdiIntegrationSettings/set', payload: integrationSettings });
      dispatch({ type: 'webEdiConfigurations/set', payload: webEdiConfigurations });
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  async function saveData() {
    if (!formRef.current?.check()) {
      return;
    }

    if (!state.integrationSettings) {
      return;
    }

    dispatch({ type: 'status/set', payload: 'saving' });
    try {
      const integrationSettings = mapEditToIntegrationSettings(state.edit);
      await saveWebEdiIntegrationSettings(integrationSettings);
      showSuccessMessage('GLN successfully saved.');
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  useEffect(() => {
    fetchData().then();
  }, []);

  function handleChange(edit: Edit) {
    dispatch({ type: 'edit/set', payload: edit });
  }

  function handleSubmit() {
    saveData().then();
  }

  if (state.status === 'loading') {
    return <Loader />;
  }

  return (
    <div>
      <br />
      <h4>WebEdi Setup</h4>
      <br />
      <Form
        layout="horizontal"
        formValue={state.edit}
        onChange={(edit) => handleChange(edit as Edit)}
        model={model}
        ref={formRef}
      >
        <Form.Group>
          <Form.ControlLabel>Receiver GLN</Form.ControlLabel>
          <Form.Control name="partnerReceiverGln" value={state.edit.partnerReceiverGln} />
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Supplier GLN</Form.ControlLabel>
          <Form.Control name="partnerSupplierGln" value={state.edit.partnerSupplierGln} />
        </Form.Group>
        {
          currentUser.hasPermission(Permission.SenderGlnNumber) && (
            <Form.Group>
              <Form.ControlLabel>Sender GLN</Form.ControlLabel>
              <Form.Control
                name="configuredGln"
                accepter={SelectPicker}
                value={state.edit.configuredGln}
                data={mapWebEdiConfigurations(state.webEdiConfigurations)}
                rule={Schema.Types.StringType().isRequired('Sender GLN is required.')}
              />
            </Form.Group>
          )
        }
        <Form.Group>
          <ButtonToolbar>
            <Button
              appearance="primary"
              onClick={() => handleSubmit()}
              loading={state.status === 'saving'}
            >
              Submit
            </Button>
          </ButtonToolbar>
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <ApiTokenReset />
          </ButtonToolbar>
        </Form.Group>
      </Form>
    </div>
  );
}
